/**
 * Pure BCP-47 matching helpers shared by the translation-memory import's
 * language validation (PLAT-10900). Kept in `utility/` rather than alongside the
 * detection `fetch` in `repositories/languageDetection.ts`: these are stateless
 * functions with no IO, and components may import them directly (a component
 * importing the repository module would breach the layer rules).
 */

/** Base BCP-47 subtag, lowercased — e.g. "es-ES" → "es", "pt-BR" → "pt". */
export function baseLanguageOf(code?: string | null): string {
  return (code ?? '').toLowerCase().split('-')[0];
}

/** Minimal shape of a workspace language option (structurally a `LanguageRef`). */
interface LanguageOption {
  id: string;
  value?: string | null;
}

/**
 * Resolve a detected code to one of the workspace's languages, matching at base
 * granularity. When several regional variants share that base (English (US) vs
 * English (UK)), a `preferredIds` entry wins over the detector's own region
 * guess: the detector infers region from a handful of short terms, while the
 * brand kit's language is a deliberate choice — so a "en-GB" guess resolves to
 * the workspace's English (US) rather than flipping the pick to UK. Falls back
 * to an exact code match, then the first same-base language.
 */
export function resolveDetectedLanguage<T extends LanguageOption>(
  code: string,
  languages: T[],
  preferredIds: (string | null | undefined)[] = [],
): T | null {
  const base = baseLanguageOf(code);
  if (!base) return null;
  const candidates = languages.filter(l => baseLanguageOf(l.value) === base);
  if (candidates.length <= 1) return candidates[0] ?? null;
  for (const id of preferredIds) {
    const preferred = id ? candidates.find(l => l.id === id) : null;
    if (preferred) return preferred;
  }
  const lower = code.toLowerCase();
  return (
    candidates.find(l => (l.value ?? '').toLowerCase() === lower) ??
    candidates[0]
  );
}
